<script
    lang="ts"
    context="module"
>
</script>

<script lang="ts">
    import Loader from "../components/Loader.svelte"
    import { deleteDBEntry, getDBEntries, getDBEntry, postDBEntry, putDBEntry } from "../../api"
    import hash from "object-hash"
    import CatchAccidentalExit from "./CatchAccidentalExit.svelte"
    import TaskStep from "./TaskStep.svelte"
    import Cta from "../components/CTA.svelte"
    import { actionApproval, login, newNotification, taskEditors } from "../store"
    import TaskImages from "./widgets/TaskImages/TaskImages.svelte"
    import TaskFiles from "./widgets/TaskFiles.svelte"
    import ManualProtocol from "./widgets/ManualProtocol.svelte"
    import { onDestroy, onMount } from "svelte"
    import StepTab from "./widgets/StepTab.svelte"
    import { getQueryParam } from "../components/formComponents/helper"
    import { spaNavigate } from "../actions"
    import {
        checkIfStepIsBlockedByOtherUser,
        checkIfStepIsCompleted,
        scrollHorizontallyToActiveTab,
        scrollToBottomOfStepsSectionIntoView,
    } from "./helper"
    import Icon from "../components/Icon.svelte"
    import { mdiChevronLeft, mdiChevronRight } from "@mdi/js"
    import { changeStep } from "../utils"
    import ActionButtonsSteps from "./widgets/ActionButtonsSteps.svelte"

    export let task: TaskOilSpillRemoval | TaskTowing
    async function checkAndUpdateTaskManipulationState(currentStepIndex: number) {
        const stepNr = currentStepIndex + 1
        try {
            const res = await getDBEntries("taskManipulationState", {
                taskId: task.id,
            })

            let tempTaskEditorsObj: TaskEditors = {}
            res.forEach((state) => {
                tempTaskEditorsObj[state.manipulator.id] = {
                    kickedOut: state.kickedOut,
                    sessionStart: new Date(state.sessionStart),
                    stepNr: state.stepNr,
                    id: state.id,
                }
            })
            if ((tempTaskEditorsObj[$login.user.id] || {})?.stepNr !== stepNr) {
                // check if someone is already editing
                let someoneIsEditing = false
                Object.values(tempTaskEditorsObj).forEach((editor: { stepNr: number }) => {
                    if (editor.stepNr === stepNr) someoneIsEditing = true
                })

                if (!someoneIsEditing && tempTaskEditorsObj[$login.user.id]?.id) {
                    await deleteDBEntry("taskManipulationState", tempTaskEditorsObj[$login.user.id]?.id)
                    await postDBEntry("taskManipulationState", {
                        taskId: task.id,
                        orderId: task.orderId,
                        manipulator: {
                            name: $login.user.username,
                            id: $login.user.id,
                        },
                        kickedOut: false,
                        sessionStart: new Date(),
                        stepNr: stepNr,
                        sort: null,
                    })

                    await checkAndUpdateTaskManipulationState(currentStepIndex)
                    task = await getDBEntry("task", {
                        _id: task.id,
                    })
                    resetOriginalTaskHash()
                    return
                } else if (!someoneIsEditing) {
                    await postDBEntry("taskManipulationState", {
                        taskId: task.id,
                        orderId: task.orderId,
                        manipulator: {
                            name: $login.user.username,
                            id: $login.user.id,
                        },
                        kickedOut: false,
                        sessionStart: new Date(),
                        stepNr: stepNr,
                        sort: null,
                    })
                    await checkAndUpdateTaskManipulationState(currentStepIndex)
                    task = await getDBEntry("task", {
                        _id: task.id,
                    })
                    resetOriginalTaskHash()
                    return
                }
            } else {
                // if the user who is in there has been in there for over 1h, find out who is blocking and kick him out: - fallback in server cron
                const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000)
                const blockingUser = Object.values(tempTaskEditorsObj).find(
                    (editor) => editor.stepNr === stepNr && editor.sessionStart < oneHourAgo
                )

                if (blockingUser) {
                    await deleteDBEntry("taskManipulationState", blockingUser.id)
                    await postDBEntry("taskManipulationState", {
                        taskId: task.id,
                        orderId: task.orderId,
                        manipulator: {
                            name: $login.user.username,
                            id: $login.user.id,
                        },
                        kickedOut: false,
                        sessionStart: new Date(),
                        stepNr: stepNr,
                        sort: null,
                    })

                    await checkAndUpdateTaskManipulationState(currentStepIndex)
                    task = await getDBEntry("task", {
                        _id: task.id,
                    })
                    resetOriginalTaskHash()
                    return
                }
            }

            if ((tempTaskEditorsObj[$login.user.id] || {})?.kickedOut) {
                await deleteDBEntry("taskManipulationState", tempTaskEditorsObj[$login.user.id].id)
                newNotification({
                    html: "Du wurdest aus dem Einsatz schritt von einem Admin entfernt",
                    class: "error",
                })
                resetOriginalTaskHash()
                delete tempTaskEditorsObj[$login.user.id]
                spaNavigate(`/order/${task.orderId}`)
            }
            if (hash($taskEditors) !== hash(tempTaskEditorsObj)) {
                taskEditors.set(tempTaskEditorsObj)
            }
        } catch (error) {
            console.error("Error updating task manipulation state:", error)
        }
    }

    onMount(() => {
        let stillRunning = true
        checkAndUpdateTaskManipulationState(selectedStep).then(() => (stillRunning = false))
        let interval = setInterval(() => {
            if (!stillRunning) {
                stillRunning = true
                checkAndUpdateTaskManipulationState(selectedStep).then(() => (stillRunning = false))
            }
        }, 5000)
        setTimeout(resetOriginalTaskHash, 500)
        return () => {
            clearInterval(interval)
        }
    })

    async function logUserOutOfCurrentStep() {
        if ($taskEditors[$login.user.id]) {
            await deleteDBEntry("taskManipulationState", $taskEditors[$login.user.id].id)
        }
    }

    let steps: TaskStep[],
        selectedStep: number = 0,
        originalTaskHash: string = hash(typeof task == "object" ? task : {}),
        originalTask: TaskOilSpillRemoval | TaskTowing = JSON.parse(JSON.stringify(task || {}))
    if (getQueryParam("selectedStep")) selectedStep = parseInt(getQueryParam("selectedStep")) - 1
    function resetOriginalTaskHash() {
        originalTaskHash = hash(typeof task == "object" ? task : {})
        originalTask = JSON.parse(JSON.stringify(task || {}))
        refreshAccidentalExit = !refreshAccidentalExit
    }

    let missingConfig = false
    const getSteps = async (taskType: string) => {
        await getDBEntries("taskStepsConfig", {
            name: taskType,
        }).then((response) => {
            if (response.length === 0) {
                missingConfig = true
                return
            }
            const taskSteps = response[0]
            if (taskSteps) steps = taskSteps.config.steps
        })
    }
    if (typeof task.meta.contactPerson !== "object") {
        task.meta.contactPerson = {
            id: undefined,
        }
    }
    //@ts-ignore
    putDBEntry("task", {
        id: task.id,
        meta: {
            ...task.meta,
            createdBy: task?.meta?.createdBy || {
                id: $login.user.id,
                name: `${$login.user.firstName} ${$login.user.lastName}`,
            },
            editedBy: [
                ...(task?.meta?.editedBy || []),
                { id: $login.user.id, name: `${$login.user.firstName} ${$login.user.lastName}` },
            ],
        },
    })
    getSteps(task.taskType).then(() => {
        setTimeout(() => {
            if (getQueryParam("selectedStep")) scrollToBottomOfStepsSectionIntoView()
            setTimeout(() => scrollHorizontallyToActiveTab(), 500)
        }, 100)
    })

    let modals = {
        showTaskImages: false,
        showTaskFiles: false,
        showManualProtocol: false,
    }

    $: if (!modals.showManualProtocol && getQueryParam("manualProtocol") === "1") {
        modals.showManualProtocol = true
        if (typeof window !== "undefined") {
            const url = new URL(window.location.href)
            url.searchParams.delete("manualProtocol")
            spaNavigate(url.pathname + (url.search ? url.search : "") + url.hash, { replace: true })
        }
    }

    function saveTask() {
        let updatedTaskObject: Partial<TaskOilSpillRemoval | TaskTowing> = {}
        updatedTaskObject.id = task.id
        // @ts-ignore
        updatedTaskObject[steps[selectedStep].pathInDb] = task[steps[selectedStep].pathInDb]
        putDBEntry("task", updatedTaskObject)
            .then(() => {
                newNotification({
                    html: "Einsatz aktualisiert",
                    class: "success",
                })
                // get latest task data
                getDBEntry("task", { _id: task.id }).then((t) => {
                    task = t
                    resetOriginalTaskHash()
                })
            })
            .catch(() => {
                newNotification({
                    html: "Fehler beim Aktualisieren des Einsatzes",
                    class: "error",
                })
            })
    }

    onDestroy(() => {
        logUserOutOfCurrentStep()
    })

    function sendBeaconOnUnload() {
        resetOriginalTaskHash()
        const params = new URLSearchParams()
        params.append("id", $taskEditors[$login.user.id]?.id)
        params.append("operation", "deleteTaskManipulation")
        params.append("jwt", $login.tokenString)
        const formData = new FormData()
        navigator.sendBeacon(`/api/action?${params.toString()}`, formData)
    }
    let rerender = false

    function finishStep() {
        $actionApproval = {
            modalTitle: "Schritt abschliessen",
            modalText: "Sind Sie sicher, dass Sie den Schritt abschliessen möchten?",
            callback: () => {
                getDBEntry("task", { _id: task.id }).then((task) => {
                    //@ts-ignore
                    const completedSteps = [
                        ...(task.completedSteps || []),
                        {
                            completedBy: $login.user.id,
                            stepNr: selectedStep + 1,
                            completedAt: new Date(),
                        },
                    ]
                    const allStepsAreCompleted = completedSteps.length === steps.length

                    const body = {
                        id: task.id,
                        completedSteps: completedSteps,
                    }
                    if (allStepsAreCompleted) body.status = "completed"

                    putDBEntry("task", body)
                        .then(() => {
                            // if not last step, go one forward
                            if (selectedStep !== steps.length - 1) changeStep(selectedStep + 1)
                            else {
                                newNotification({
                                    html: "Einsatz abgeschlossen",
                                    class: "success",
                                })
                                window.location.reload()
                            }
                        })
                        .catch((e) => {
                            console.log(e, "error")
                            newNotification({
                                html: "Fehler beim Abschließen des Schrittes",
                                class: "error",
                            })
                        })
                })
            },
        }
    }
    let refreshAccidentalExit = false
    let innerWidth = 0
    $: isMobile = innerWidth < 1700
</script>

<svelte:window
    bind:innerWidth="{innerWidth}"
    on:unload="{() => {
        sendBeaconOnUnload()
    }}"
    on:visibilitychange="{() => {
        if (document.visibilityState === 'hidden') sendBeaconOnUnload()
    }}"
/>
{#if !steps && !missingConfig}
    <Loader />
{:else if missingConfig}
    <div style="display: flex;flex-direction: column; width: 100%; align-items: flex-start; padding: 1.2rem;">
        <h2>Keine Konfiguration gefunden</h2>
        <p>Es wurde keine Konfiguration für diesen Einsatztypen gefunden.</p>
    </div>
{:else}
    <div class="lower-step-config-part">
        {#if !isMobile}
            <div class="task-steps-tabs">
                {#key rerender}
                    {#if steps}
                        {#each steps as step, i}
                            <StepTab
                                stepIndex="{i}"
                                task="{task}"
                                selectedStep="{selectedStep}"
                                changeStep="{changeStep}"
                                step="{step}"
                            />
                        {/each}
                    {/if}
                {/key}
            </div>
        {:else}
            <div
                class="mobile-tab-switch"
                style="border-bottom: 1px solid {checkIfStepIsCompleted(task, selectedStep + 1)
                    ? 'var(--confirm-100)'
                    : checkIfStepIsBlockedByOtherUser(selectedStep + 1, $taskEditors)
                    ? 'var(--red-100)'
                    : 'var(--grey-300)'};"
            >
                {#if selectedStep !== 0}
                    <button
                        on:click="{() => {
                            changeStep(selectedStep - 1)
                        }}"
                    >
                        <Icon
                            path="{mdiChevronLeft}"
                            size="2rem"
                        />
                    </button>
                {/if}
                <StepTab
                    stepIndex="{selectedStep}"
                    task="{task}"
                    selectedStep="{selectedStep}"
                    changeStep="{changeStep}"
                    step="{steps[selectedStep]}"
                />
                {#if selectedStep !== steps.length - 1}
                    <button
                        on:click="{() => {
                            changeStep(selectedStep + 1)
                        }}"
                    >
                        <Icon
                            path="{mdiChevronRight}"
                            size="2rem"
                        />
                    </button>
                {/if}
            </div>
        {/if}
        <div class="task-steps form">
            {#key rerender}
                {#if steps}
                    {#each steps as step, i}
                        {#if i == selectedStep}
                            <TaskStep
                                checkAndUpdateTaskManipulationState="{checkAndUpdateTaskManipulationState}"
                                step="{step}"
                                bind:task="{task}"
                                stepNr="{i + 1}"
                            />
                        {/if}
                    {/each}
                {/if}
            {/key}
        </div>
    </div>

    <div class="actionRow">
        {#if !isMobile}
            {#if selectedStep !== 0}
                <div>
                    <Cta
                        type="tertiaryButton"
                        icon="solar_arrow-left-outline"
                        content="{isMobile ? '' : 'Zurück'}"
                        on:click="{() => {
                            changeStep(selectedStep - 1)
                        }}"
                    />
                </div>
            {/if}

            <div>
                <ActionButtonsSteps
                    bind:modals="{modals}"
                    ghostButton="{true}"
                    hashChanged="{!(
                        checkIfStepIsCompleted(task, selectedStep + 1) ||
                        checkIfStepIsBlockedByOtherUser(selectedStep + 1, $taskEditors) ||
                        originalTaskHash === hash(task)
                    )}"
                />
            </div>
        {/if}
        <div>
            {#if isMobile && selectedStep !== 0}
                <div>
                    <Cta
                        type="tertiaryButton"
                        icon="solar_arrow-left-outline"
                        content="{isMobile ? '' : 'Zurück'}"
                        on:click="{() => {
                            spaNavigate(`/order/${task.orderId}/task/${task.id}?selectedStep=${selectedStep}`)
                        }}"
                    />
                </div>
            {/if}
            {#key rerender}
                <Cta
                    type="primaryButton"
                    icon="solar_diskette-outline"
                    content="{isMobile ? '' : 'Speichern'}"
                    disabled="{checkIfStepIsCompleted(task, selectedStep + 1) ||
                        checkIfStepIsBlockedByOtherUser(selectedStep + 1, $taskEditors) ||
                        originalTaskHash === hash(task)}"
                    on:click="{saveTask}"
                />
                <Cta
                    type="confirmationButton"
                    icon="solar_check-read-outline"
                    content="{isMobile ? '' : 'Abschließen'}"
                    disabled="{checkIfStepIsCompleted(task, selectedStep + 1) ||
                        checkIfStepIsBlockedByOtherUser(selectedStep + 1, $taskEditors) ||
                        originalTaskHash !== hash(task)}"
                    on:click="{finishStep}"
                />
            {/key}
            {#if steps.length - 1 !== selectedStep}
                <Cta
                    type="tertiaryButton"
                    icon="solar_arrow-right-outline"
                    content="{isMobile ? '' : 'Weiter'}"
                    on:click="{() => changeStep(selectedStep + 1)}"
                />
            {/if}
        </div>
    </div>
    {#if isMobile}
        <div class="row-second-cta">
            <ActionButtonsSteps
                bind:modals="{modals}"
                ghostButton="{false}"
                hashChanged="{!(
                    checkIfStepIsCompleted(task, selectedStep + 1) ||
                    checkIfStepIsBlockedByOtherUser(selectedStep + 1, $taskEditors) ||
                    originalTaskHash === hash(task)
                )}"
            />
        </div>
    {/if}
    <div class="action-row-placeholder"></div>
    {#if modals.showTaskImages}
        <TaskImages
            recoverImages="{true}"
            task="{JSON.parse(JSON.stringify(task))}"
            on:updateTaskImages="{(event) => {
                task.images = event.detail
                resetOriginalTaskHash()
            }}"
            bind:showModal="{modals.showTaskImages}"
        />
    {/if}
    {#if modals.showTaskFiles}
        <TaskFiles
            task="{JSON.parse(JSON.stringify(task))}"
            on:updateTaskFiles="{(event) => {
                task.files = event.detail
                resetOriginalTaskHash()
            }}"
            bind:showModal="{modals.showTaskFiles}"
        />
    {/if}
    {#if modals.showManualProtocol}
        <ManualProtocol
            task="{JSON.parse(JSON.stringify(task))}"
            on:updateTaskProtocol="{(event) => {
                task.manualProtocol = event.detail
                resetOriginalTaskHash()
            }}"
            bind:showModal="{modals.showManualProtocol}"
        />
    {/if}
{/if}

{#if task}
    {#key refreshAccidentalExit}
        <CatchAccidentalExit
            bind:originalTaskHash="{originalTaskHash}"
            task="{task}"
            bind:originalTask="{originalTask}"
        />
    {/key}
{/if}

<style lang="less">
    @import "../../../assets/variables.less";
    :global .mobile-tab-switch {
        display: flex;
        justify-content: space-between;
        margin-top: 0.6rem;
        border-bottom: 1px solid var(--grey-300);
        .tab {
            flex-grow: 1;
        }
        button {
            color: var(--text-100);
        }
    }
    .task-steps-tabs,
    .task-steps {
        display: flex;
        width: 100%;
    }
    .task-steps-tabs {
        border-bottom: 1px solid var(--grey-300);

        margin-top: 0.6rem;
        overflow-x: scroll;
        -ms-overflow-style: none; /* Internet Explorer 10+ */
        scrollbar-width: none; /* Firefox */

        &::-webkit-scrollbar {
            display: none; /* Safari and Chrome */
        }
    }
    .actionRow {
        display: flex;
        position: relative;
        bottom: 0;
        height: 82px;
        justify-content: space-between;
        padding: 0.6rem 1.2rem;
        margin-bottom: -2px;
        width: 100%;
        background-color: var(--grey-300);

        div {
            display: flex;
            gap: 0.6rem;
        }
    }
    .lower-step-config-part {
        width: 100%;
        position: relative;
    }
    @media @mobile {
        .row-second-cta {
            display: flex;
            width: 100%;
            justify-content: space-between;
            padding: 0rem 1.2rem;
        }
        .lower-step-config-part {
            max-height: 60vh;

            overflow-y: auto;
            width: 100%;
            position: relative;
        }
        .action-row-placeholder {
            height: 82px;
        }
        .actionRow {
            position: absolute;
            bottom: 0px;
            & > div:last-child {
                flex-grow: 1;
                justify-content: space-around;
            }
        }
    }
</style>
